/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import axios from "axios";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { provisioningTypes } from "../common/provisioningTypes";

const ProductMetaDataSchema = z.object({
    /** Internal name of the product */
    productName: z.string().min(1),
    /** Product Guid */
    productGuid: z.string().min(1),
    /** Hierarchical setting definition name for the product */
    definitionName: z.string().optional(),
    /** The product provisioning type */
    provisioningType: z.nativeEnum(provisioningTypes).optional(),
    /** Is auto provisioning enabled by default */
    defaultAutoProvisioning: z.boolean().optional(),
    /** Does this product allow license restrictions */
    allowLicenseRestriction: z.boolean().optional()
});

/**
 * NCCv2 Metadata for a product
 */
export type ProductMetaData = z.infer<typeof ProductMetaDataSchema>;

/**
 * Application configuration data.
 *
 * @see config-schema.json Please make sure you keep both files  in-sync!
 */
const AppConfigDataSchema = z.object({
    /** environment type */
    environment: z.string(),
    /** Application Insights connection string */
    appInsightsConnectionString: z.string().min(1),
    /** An optional array of services that corresponding UI should not be rendered for. */
    disabledServices: z.array(z.string()).optional(),
    /** Auth0 configuration information */
    auth0Config: z
        .object({
            /** Auth0 domain to use for authentication */
            domain: z.string().min(1),
            /** Auth0 client identified */
            clientId: z.string().min(1),
            /** Audience associated with the application */
            audience: z.string().min(1),
            /** NMS product guid for the application */
            productGuid: z.string().min(1),
            /** application name */
            nmsCallerId: z.string().min(1)
        })
        .optional(),
    /** AAD configuration information */
    aadConfig: z
        .object({
            /** Entra id authority */
            authority: z.string().min(1).optional(),
            /** AAD client identifier */
            clientId: z.string().min(1),
            /** optional AAD scopes */
            scopes: z.array(z.string().min(1)).optional(),
            /** NMS product guid for the application */
            productGuid: z.string().min(1),
            /** application name */
            nmsCallerId: z.string().min(1)
        })
        .optional(),
    /** FedRamp ADFS configuration. */
    ADFSConfig: z
        .object({
            /** ADFS client identifier */
            clientId: z.string().min(1),
            /** ADFS authority */
            authority: z.string().min(1),
            /** optional AAD scopes */
            scopes: z.array(z.string().min(1)).optional(),
            /** NMS product guid for the application */
            productGuid: z.string().min(1),
            /** NMS called id */
            nmsCallerId: z.string().min(1),
            /** Resource */
            resource: z.string().optional()
        })
        .optional(),
    /** URL to Power BI reports */
    powerBiEmbedUrl: z.string().min(1),
    /** Feature set */
    featureSet: z.number().min(0).max(3),
    /** Application Metadata for products */
    productsMetaData: ProductMetaDataSchema.array()
});

/** Application configuration information */
export type AppConfig = z.infer<typeof AppConfigDataSchema>;

/**
 * Name of the meta tag whose content is replaced at container start with the
 * base64-encoded, environment-specific application configuration.
 */
const APP_CONFIG_META_NAME = "app-config";

/**
 * Sentinel value present in the meta tag when no configuration has been
 * injected (e.g. local development or test, where the placeholder is left
 * untouched). Must match the placeholder in public/index.html.
 */
const APP_CONFIG_PLACEHOLDER = "RUNTIME_APP_CONFIG_PLACEHOLDER";

/**
 * Read the application configuration injected into the page at container start.
 * Returns undefined when nothing has been injected so the caller can fall back
 * to fetching the configuration (used in local development and tests).
 * @returns The injected configuration object, or undefined.
 */
function readInjectedAppConfig(): unknown | undefined {
    const meta = document.querySelector(`meta[name="${APP_CONFIG_META_NAME}"]`);
    const content = meta?.getAttribute("content");
    if (!content || content === APP_CONFIG_PLACEHOLDER) {
        return undefined;
    }
    return JSON.parse(atob(content));
}

/**
 * Get the application configuration. Prefers the configuration injected into
 * the page at container start; falls back to an HTTP request when none has
 * been injected (local development and tests).
 * @returns Application configuration
 */
export async function getAppConfig(): Promise<AppConfig> {
    const injected = readInjectedAppConfig();
    if (injected !== undefined) {
        return AppConfigDataSchema.parse(injected);
    }
    const response = await axios.get("/config.json");
    const result = AppConfigDataSchema.parse(response.data);
    return result;
}

/**
 * Hook to get (and cache) the application configuration
 * @returns Application configuration
 */
export function useAppConfig() {
    return useQuery<AppConfig>({
        queryKey: ["config"],
        queryFn: getAppConfig,
        // don't need to refetch this as the config is in the source code and will not change during a session.
        staleTime: Infinity
    });
}
